/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { Loader2, Plus, Download, BarChart3, Printer, FileText, Upload, Settings, Eye, EyeOff, Send, Maximize, Minimize } from 'lucide-react';
import { useToast } from '../ui/use-toast';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import GradebookSettings from './GradebookSettings';
import ClassReportDialog from './ClassReportDialog';

/**
 * GradebookView Component
 *
 * Spreadsheet-style gradebook interface like Google Classroom:
 * - Clean grid layout with students vs assignments
 * - Direct click-to-edit grade entry
 * - Auto-calculated final grades
 * - Minimal, intuitive interface
 */
const GradebookView = ({ user }) => {
  const { toast } = useToast();
  const inputRef = useRef(null);

  // State management
  const [selectedCohort, setSelectedCohort] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [gradebookData, setGradebookData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStats, setShowStats] = useState(false); // Hide stats by default
  const [showKeyboardHints, setShowKeyboardHints] = useState(true); // Show keyboard shortcuts
  const [isFullscreen, setIsFullscreen] = useState(false); // Fullscreen mode
  const [sortBy, setSortBy] = useState('lastName'); // lastName, firstName, studentId
  const [searchFilter, setSearchFilter] = useState(''); // Search/filter students

  // Grade editing
  const [editingCell, setEditingCell] = useState(null); // { studentId, assignmentId }
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingCells, setSavingCells] = useState(new Set()); // Track cells being saved
  const [savedCells, setSavedCells] = useState(new Set()); // Track recently saved cells

  // Assignment management
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [showEditAssignment, setShowEditAssignment] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [updatingAssignment, setUpdatingAssignment] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [categories, setCategories] = useState([]);

  // Settings management
  const [showSettings, setShowSettings] = useState(false);

  // Class report
  const [showClassReport, setShowClassReport] = useState(false);

  // Form data
  const [assignmentForm, setAssignmentForm] = useState({
    name: '',
    description: '',
    category_id: '',
    points_possible: 100,
    due_date: '',
    is_hidden_from_students: false,
    allows_submissions: false,
    submission_types: []
  });

  // Form validation
  const [formErrors, setFormErrors] = useState({});

  // Load teacher's cohorts on mount
  useEffect(() => {
    fetchCohorts();
  }, []);

  // Load gradebook data when cohort is selected
  useEffect(() => {
    if (selectedCohort) {
      fetchGradebookData();
      fetchCategories();
    }
  }, [selectedCohort]);

  // Keyboard shortcuts for horizontal scrolling
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Shift + Left/Right arrows to scroll horizontally
      if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        const scrollContainer = document.querySelector('.gradebook-table-scroll');
        if (scrollContainer) {
          const scrollAmount = 200; // pixels to scroll
          scrollContainer.scrollBy({
            left: e.key === 'ArrowLeft' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fullscreen mode handlers
  const toggleFullscreen = async () => {
    if (!isFullscreen) {
      // Enter fullscreen
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { // Safari
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { // IE11
          await elem.msRequestFullscreen();
        }
        setIsFullscreen(true);
      } catch (error) {
        console.error('Error entering fullscreen:', error);
      }
    } else {
      // Exit fullscreen
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { // Safari
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { // IE11
          await document.msExitFullscreen();
        }
        setIsFullscreen(false);
      } catch (error) {
        console.error('Error exiting fullscreen:', error);
      }
    }
  };

  // Listen for fullscreen changes (e.g., user pressing Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const fetchCohorts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/cohorts`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch cohorts');

      const data = await response.json();
      setCohorts(data);

      // Auto-select first cohort if available
      if (data.length > 0 && !selectedCohort) {
        setSelectedCohort(data[0].id.toString());
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load classes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGradebookData = async () => {
    if (!selectedCohort) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/cohort/${selectedCohort}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch gradebook data');

      const data = await response.json();
      setGradebookData(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load gradebook data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!selectedCohort) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/cohort/${selectedCohort}/categories`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch categories');

      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const calculateStudentGrade = (studentId) => {
    if (!gradebookData) {
      return { finalGrade: 0, letterGrade: 'F' };
    }

    const studentEntries = gradebookData.entries.filter(entry => entry.student_id === studentId);

    if (studentEntries.length === 0) {
      return { finalGrade: 0, letterGrade: 'F' };
    }

    // Group by category and calculate weighted average
    const categoryGrades = {};

    // Build category weights from assignments' category_weight field (live data)
    // This ensures we always use current weights, even if settings are stale
    const categoryWeights = {};

    // First pass: collect all category weights from assignments
    gradebookData.assignments.forEach(assignment => {
      if (assignment.category_name && assignment.category_weight !== null && assignment.category_weight !== undefined) {
        categoryWeights[assignment.category_name] = parseFloat(assignment.category_weight);
      }
    });

    // Fallback to settings if no weights found in assignments
    if (Object.keys(categoryWeights).length === 0) {
      const settingsWeights = gradebookData.settings?.default_weights || {
        'Safety': 25,
        'Projects': 50,
        'Classwork': 25
      };
      Object.assign(categoryWeights, settingsWeights);
    }

    studentEntries.forEach(entry => {
      const assignment = gradebookData.assignments.find(a => a.id === entry.assignment_id);
      if (!assignment) {
        return;
      }

      const categoryName = assignment.category_name || 'Uncategorized';
      if (!categoryGrades[categoryName]) {
        categoryGrades[categoryName] = [];
      }

      // Skip excused assignments (don't count them)
      if (entry.status === 'exc') {
        return;
      }

      // For 'nhi' status, percentage should be 0
      if (entry.status === 'nhi') {
        categoryGrades[categoryName].push(0);
      } else if (entry.percentage !== null && entry.percentage !== undefined) {
        categoryGrades[categoryName].push(entry.percentage);
      }
    });

    // Calculate weighted final grade
    let totalWeightedPoints = 0;
    let totalWeight = 0;

    // Calculate the sum of ALL category weights (from assignments), not just categories with grades
    Object.entries(categoryGrades).forEach(([category, grades]) => {
      if (grades.length > 0) {
        const average = grades.reduce((sum, grade) => {
          const numGrade = parseFloat(grade);
          return sum + numGrade;
        }, 0) / grades.length;

        // Get weight from categoryWeights (now built from live assignment data)
        let weight = categoryWeights[category];
        if (weight === undefined || weight === null) {
          weight = 0;
        }

        totalWeightedPoints += average * (weight / 100);
        totalWeight += weight;
      }
    });

    // If student has grades but not in all categories, calculate based on what they've done
    // Use totalWeight (what they have) instead of totalAvailableWeight (what exists)
    const finalGrade = totalWeight > 0 ? (totalWeightedPoints / (totalWeight / 100)) : 0;

    // Determine letter grade using grading scale from settings
    let letterGrade = 'F';

    // Use stored grading scale if available, otherwise use defaults
    const displayMode = gradebookData.settings?.display_mode || 'percentage';
    const gradingScale = gradebookData.settings?.grading_scale;

    if (gradingScale && typeof gradingScale === 'object') {
      // Use the stored grading scale
      // Sort grading scale by min value (descending) - check highest grades first
      // This ensures A (86-100) is checked before I (0-49)
      const sortedGrades = Object.entries(gradingScale)
        .sort((a, b) => parseFloat(b[1].min) - parseFloat(a[1].min));

      // Find the appropriate grade by checking sorted ranges
      for (const [grade, range] of sortedGrades) {
        // Convert to numbers to handle string/number type mismatches
        const gradeMin = parseFloat(range.min);
        const gradeMax = parseFloat(range.max);

        if (finalGrade >= gradeMin && finalGrade <= gradeMax) {
          letterGrade = grade;
          break;
        }
      }

      // Better fallback: find highest grade where finalGrade >= min
      if (letterGrade === 'F') {
        for (const [grade, range] of sortedGrades) {
          if (finalGrade >= parseFloat(range.min)) {
            letterGrade = grade;
            break;
          }
        }
      }
    } else {
      // Fallback to hardcoded defaults if no stored scale available
      if (displayMode === 'proficiency') {
        // Proficiency Levels mode - use proficiency scale
        if (finalGrade >= 90) letterGrade = 'Extending';
        else if (finalGrade >= 60) letterGrade = 'Proficient';
        else if (finalGrade >= 30) letterGrade = 'Developing';
        else if (finalGrade >= 15) letterGrade = 'Emerging';
        else letterGrade = 'IE';
      } else {
        // Percentage & Letter Grades mode - use A-F scale
        if (finalGrade >= 86) letterGrade = 'A';
        else if (finalGrade >= 73) letterGrade = 'B';
        else if (finalGrade >= 67) letterGrade = 'C+';
        else if (finalGrade >= 60) letterGrade = 'C';
        else if (finalGrade >= 50) letterGrade = 'C-';
        else letterGrade = 'I';
      }
    }

    return {
      finalGrade: gradebookData.settings?.round_final_grades ? Math.round(finalGrade) : Math.round(finalGrade * 10) / 10,
      letterGrade
    };
  };

  // Export functions
  const exportToCSV = () => {
    if (!gradebookData || !gradebookData.students || !gradebookData.assignments) {
      toast({
        title: "Error",
        description: "No data to export",
        variant: "destructive"
      });
      return;
    }

    const csvData = [];
    const headers = ['Student Name', 'Student ID', ...gradebookData.assignments.map(a => a.name), 'Final Grade', 'Letter Grade'];
    csvData.push(headers);

    gradebookData.students.forEach(student => {
      const row = [
        `${student.first_name} ${student.last_name}`,
        student.student_id
      ];

      // Add assignment grades
      gradebookData.assignments.forEach(assignment => {
        const grade = getAssignmentGrade(student.id, assignment.id);
        row.push(grade?.percentage?.toString() || '');
      });

      // Add final grade
      const { finalGrade, letterGrade } = calculateStudentGrade(student.id);
      row.push(finalGrade.toString());
      row.push(letterGrade);

      csvData.push(row);
    });

    // Convert to CSV string
    const csvString = csvData.map(row =>
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    // Download CSV
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `gradebook_${gradebookData.cohort?.name || 'class'}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    toast({
      title: "Export Complete",
      description: "Gradebook data exported to CSV successfully"
    });
  };

  const handlePrint = () => {
    if (!gradebookData || !gradebookData.students || !gradebookData.assignments) {
      console.error('Missing gradebook data for print');
      return;
    }

    try {
      console.log('Starting print generation...');
      // Generate complete HTML with ALL students and assignments
      const printWindow = window.open('', '_blank');

      if (!printWindow) {
        alert('Please allow pop-ups to print the gradebook');
        return;
      }

    // Sort students
    const sortedStudents = [...gradebookData.students].sort((a, b) =>
      a.last_name.localeCompare(b.last_name)
    );

    // Generate table rows
    let tableRows = '';
    sortedStudents.forEach(student => {
      const { finalGrade, letterGrade } = calculateStudentGrade(student.id);

      let row = `
        <tr>
          <td style="position: sticky; left: 0; background: white; font-weight: 500; padding: 6px 8px; border-right: 2px solid #cbd5e1;">
            ${student.first_name} ${student.last_name}<br>
            <span style="font-size: 9px; color: #64748b;">${student.student_id || ''}</span>
          </td>
      `;

      gradebookData.assignments.forEach(assignment => {
        const grade = getAssignmentGrade(student.id, assignment.id);
        const percentage = grade && grade.percentage !== null ? Math.round(grade.percentage) : '-';
        const bgColor = grade?.is_late ? '#fef3c7' : (grade?.is_excused ? '#dbeafe' : 'white');

        row += `<td style="text-align: center; padding: 4px; background: ${bgColor};">${percentage !== '-' ? percentage + '%' : '-'}</td>`;
      });

      const gradeColor = finalGrade >= 80 ? '#059669' : finalGrade >= 70 ? '#d97706' : '#dc2626';
      row += `
        <td style="position: sticky; right: 0; background: #eff6ff; font-weight: 600; text-align: center; padding: 6px 8px; color: ${gradeColor}; border-left: 2px solid #cbd5e1;">
          ${Math.round(finalGrade)}% (${letterGrade})
        </td>
      </tr>`;

      tableRows += row;
    });

    // Generate assignment headers
    let assignmentHeaders = '';
    gradebookData.assignments.forEach(assignment => {
      const tooltip = `${assignment.name}\\n${assignment.points_possible} pts\\n${assignment.category_name || ''}`;
      assignmentHeaders += `
        <th style="writing-mode: vertical-rl; text-orientation: mixed; padding: 8px 4px; font-size: 9px; max-width: 30px; border-right: 1px solid #e5e7eb;" title="${tooltip}">
          <div style="display: flex; flex-direction: column; align-items: center;">
            <span style="margin-bottom: 4px;">${assignment.name}</span>
            <span style="color: #64748b;">${assignment.points_possible}pts</span>
          </div>
        </th>
      `;
    });

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Gradebook - ${gradebookData.cohort?.name || 'Class'}</title>
          <style>
            @page {
              size: landscape;
              margin: 0.4in;
            }

            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 10px;
              margin: 0;
              padding: 0;
            }

            .header {
              text-align: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #2563eb;
            }

            .header h1 {
              margin: 0 0 5px 0;
              font-size: 18px;
              color: #1e40af;
            }

            .header .date {
              font-size: 10px;
              color: #64748b;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9px;
            }

            thead {
              background: #f1f5f9;
            }

            th {
              padding: 8px 6px;
              text-align: left;
              font-weight: 600;
              color: #475569;
              border-bottom: 2px solid #cbd5e1;
              border-right: 1px solid #e5e7eb;
            }

            td {
              padding: 4px 6px;
              border-bottom: 1px solid #e5e7eb;
              border-right: 1px solid #f1f5f9;
            }

            tbody tr:nth-child(even) {
              background: #f8fafc;
            }

            tbody tr:hover {
              background: #e0f2fe;
            }

            .student-cell {
              position: sticky;
              left: 0;
              background: white;
              font-weight: 500;
              min-width: 120px;
              max-width: 120px;
              border-right: 2px solid #cbd5e1 !important;
            }

            .final-grade-header {
              position: sticky;
              right: 0;
              background: #eff6ff;
              border-left: 2px solid #cbd5e1 !important;
              text-align: center;
              min-width: 80px;
            }

            .stats {
              margin-top: 15px;
              padding-top: 10px;
              border-top: 1px solid #e5e7eb;
              display: flex;
              justify-content: space-around;
              font-size: 10px;
            }

            .stat {
              text-align: center;
            }

            .stat-value {
              font-size: 16px;
              font-weight: bold;
              color: #1e40af;
            }

            .stat-label {
              color: #64748b;
              margin-top: 2px;
            }

            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #2563eb;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              z-index: 1000;
            }

            .print-button:hover {
              background: #1e40af;
            }

            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
              .print-button { display: none !important; }
            }
          </style>
        </head>
        <body>
          <button class="print-button" onclick="window.print()">🖨️ Print Gradebook</button>

          <div class="header">
            <h1>Gradebook: ${gradebookData.cohort?.name || 'Class'}</h1>
            <div class="date">Printed: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th class="student-cell">Student Name</th>
                ${assignmentHeaders}
                <th class="final-grade-header">Final Grade</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>

          <div class="stats">
            <div class="stat">
              <div class="stat-value">${gradebookData.students.length}</div>
              <div class="stat-label">Students</div>
            </div>
            <div class="stat">
              <div class="stat-value">${gradebookData.assignments.length}</div>
              <div class="stat-label">Assignments</div>
            </div>
            <div class="stat">
              <div class="stat-value">${Math.round(gradebookData.students.reduce((sum, s) => {
                return sum + calculateStudentGrade(s.id).finalGrade;
              }, 0) / gradebookData.students.length)}%</div>
              <div class="stat-label">Class Average</div>
            </div>
          </div>
        </body>
      </html>
    `;

      console.log('HTML generated, writing to window...');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();

      // Trigger print dialog automatically, but don't close window
      setTimeout(() => {
        printWindow.print();
      }, 500);

      console.log('Print window opened successfully');
    } catch (error) {
      console.error('Error generating print:', error);
      alert('Error generating print: ' + error.message);
    }
  };

  const handlePrintAllReports = () => {
    if (!gradebookData || !gradebookData.students || gradebookData.students.length === 0) return;

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow pop-ups to view all student reports');
        return;
      }

      // Sort students by last name
      const sortedStudents = [...gradebookData.students].sort((a, b) =>
        a.last_name.localeCompare(b.last_name)
      );

      // Generate HTML for all students
      let allReportsHtml = '';

      sortedStudents.forEach((student, index) => {
        const reportHtml = generateStudentReportHTML(student);
        // Add page break after each report except the last one
        const pageBreak = index < sortedStudents.length - 1 ? '<div style="page-break-after: always;"></div>' : '';
        allReportsHtml += reportHtml + pageBreak;
      });

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>All Student Reports - ${gradebookData.cohort?.name || 'Class'}</title>
            <style>
              @page { size: letter portrait; margin: 0.5in; }
              body {
                font-family: 'Arial', 'Helvetica', sans-serif;
                line-height: 1.4;
                color: #1f2937;
                max-width: 8.5in;
                margin: 0 auto;
                padding: 20px;
                background: white;
              }
              .report-header {
                text-align: center;
                border-bottom: 3px solid #2563eb;
                padding-bottom: 15px;
                margin-bottom: 20px;
              }
              .report-header h1 {
                font-size: 24px;
                color: #1e40af;
                margin: 0 0 5px 0;
              }
              .report-header h2 {
                font-size: 18px;
                color: #475569;
                margin: 5px 0;
                font-weight: normal;
              }
              .info-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
                margin-bottom: 20px;
                padding: 15px;
                background: #f8fafc;
                border-radius: 8px;
              }
              .info-item { padding: 5px 0; }
              .info-label {
                font-weight: 600;
                color: #64748b;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .info-value {
                font-size: 16px;
                color: #1f2937;
                margin-top: 2px;
              }
              .grade-summary {
                background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
                color: white;
                padding: 20px;
                border-radius: 8px;
                margin-bottom: 20px;
                text-align: center;
              }
              .grade-summary h3 {
                margin: 0 0 10px 0;
                font-size: 16px;
                opacity: 0.9;
              }
              .grade-display {
                display: flex;
                justify-content: center;
                align-items: baseline;
                gap: 15px;
              }
              .grade-number {
                font-size: 48px;
                font-weight: bold;
              }
              .grade-letter {
                font-size: 32px;
                font-weight: 600;
              }
              .class-comparison {
                font-size: 13px;
                margin-top: 10px;
                opacity: 0.9;
              }
              .section {
                margin-bottom: 25px;
                page-break-inside: avoid;
              }
              .section-title {
                font-size: 16px;
                font-weight: 600;
                color: #1e40af;
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 5px;
                margin-bottom: 12px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 11px;
              }
              thead { background: #f1f5f9; }
              th {
                text-align: left;
                padding: 8px;
                font-weight: 600;
                color: #475569;
                border-bottom: 2px solid #cbd5e1;
              }
              td {
                padding: 6px 8px;
                border-bottom: 1px solid #e5e7eb;
              }
              tbody tr:hover { background: #f8fafc; }
              .grade-cell {
                text-align: center;
                font-weight: 600;
              }
              .grade-good { color: #059669; }
              .grade-warning { color: #d97706; }
              .grade-poor { color: #dc2626; }
              .category-badge {
                display: inline-block;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 10px;
                font-weight: 600;
                background: #e0e7ff;
                color: #3730a3;
              }
              .missing-badge {
                background: #fee2e2;
                color: #991b1b;
              }
              .late-badge {
                background: #fed7aa;
                color: #9a3412;
              }
              .category-stats {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 12px;
                margin-bottom: 15px;
              }
              .category-card {
                background: #f8fafc;
                padding: 12px;
                border-radius: 6px;
                border-left: 4px solid #3b82f6;
              }
              .category-card h4 {
                margin: 0 0 8px 0;
                font-size: 13px;
                color: #1e40af;
              }
              .category-percentage {
                font-size: 24px;
                font-weight: bold;
                color: #1f2937;
              }
              .category-detail {
                font-size: 11px;
                color: #64748b;
                margin-top: 4px;
              }
              .no-data {
                text-align: center;
                color: #94a3b8;
                padding: 20px;
                font-style: italic;
              }
              .footer {
                margin-top: 30px;
                padding-top: 15px;
                border-top: 1px solid #e5e7eb;
                font-size: 11px;
                color: #64748b;
                text-align: center;
              }
              .print-button {
                position: fixed;
                top: 20px;
                right: 20px;
                background: #2563eb;
                color: white;
                border: none;
                padding: 10px 20px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 600;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                z-index: 1000;
              }
              .print-button:hover {
                background: #1e40af;
              }
              @media print {
                body { padding: 0; }
                .print-button { display: none !important; }
              }
            </style>
          </head>
          <body>
            <button class="print-button" onclick="window.print()">🖨️ Print All Reports (${sortedStudents.length} students)</button>
            ${allReportsHtml}
          </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();

      // Trigger print dialog automatically
      setTimeout(() => {
        printWindow.print();
      }, 1000); // Slightly longer delay for multiple reports
    } catch (error) {
      console.error('Error generating all reports:', error);
      alert('Error generating reports: ' + error.message);
    }
  };

  const generateStudentReportHTML = (student) => {
    if (!student || !gradebookData) return '';

    const { finalGrade, letterGrade } = calculateStudentGrade(student.id);

    // Get student's grades
    const studentGrades = gradebookData.entries
      ?.filter(entry => entry.student_id === student.id)
        .map(entry => {
          const assignment = gradebookData.assignments.find(a => a.id === entry.assignment_id);
          // Skip if assignment not found (deleted)
          if (!assignment) return null;

          return {
            ...entry,
            assignment_name: assignment.name,
            assignment_points: assignment.points_possible || 0,
            category_name: assignment.category_name || 'Uncategorized',
            due_date: assignment.due_date,
            is_test: assignment.is_test_assignment
          };
        })
        .filter(grade => grade !== null) // Remove null entries (deleted assignments)
        .sort((a, b) => {
          if (a.category_name !== b.category_name) {
            return a.category_name.localeCompare(b.category_name);
          }
          if (a.due_date && b.due_date) {
            return new Date(b.due_date) - new Date(a.due_date);
          }
          return 0;
        }) || [];

    // Calculate category breakdown
    const categories = {};
    studentGrades.forEach(grade => {
      if (!categories[grade.category_name]) {
        categories[grade.category_name] = {
          total_points: 0,
          earned_points: 0,
          count: 0,
          percentage: 0
        };
      }
      if (grade.percentage !== null) {
        const assignmentPoints = parseFloat(grade.assignment_points) || 0;
        const pointsEarned = parseFloat(grade.points_earned) || 0;
        categories[grade.category_name].total_points += assignmentPoints;
        categories[grade.category_name].earned_points += pointsEarned;
        categories[grade.category_name].count += 1;
      }
    });

    Object.keys(categories).forEach(cat => {
      if (categories[cat].total_points > 0) {
        categories[cat].percentage = Math.round((categories[cat].earned_points / categories[cat].total_points) * 100);
      }
    });

    // Find missing assignments
    const missingAssignments = gradebookData.assignments
      ?.filter(assignment => {
        const entry = gradebookData.entries?.find(e =>
          e.student_id === student.id && e.assignment_id === assignment.id
        );
        return !entry || (entry.percentage === null && entry.status !== 'excused');
      })
      .slice(0, 5);

    // Calculate class average
    const classAverage = gradebookData.students?.length > 0
      ? Math.round(gradebookData.students.reduce((sum, s) => {
          const grade = calculateStudentGrade(s.id);
          return sum + grade.finalGrade;
        }, 0) / gradebookData.students.length)
      : 0;

    // Generate category cards HTML
    let categoryCardsHtml = '';
    Object.entries(categories).forEach(([name, stats]) => {
      categoryCardsHtml += `
        <div class="category-card">
          <h4>${name}</h4>
          <div class="category-percentage">${stats.percentage}%</div>
          <div class="category-detail">
            ${stats.earned_points.toFixed(1)} / ${stats.total_points.toFixed(1)} points (${stats.count} assignment${stats.count !== 1 ? 's' : ''})
          </div>
        </div>
      `;
    });

    // Generate assignment rows HTML
    let assignmentRowsHtml = '';
    studentGrades.forEach((grade, idx) => {
      const percentage = grade.percentage || 0;
      const gradeClass = percentage >= 80 ? 'grade-good' : percentage >= 70 ? 'grade-warning' : 'grade-poor';

      assignmentRowsHtml += `
        <tr>
          <td>${grade.assignment_name}</td>
          <td><span class="category-badge">${grade.category_name}</span></td>
          <td class="grade-cell">
            ${grade.points_earned !== null ? `${parseFloat(grade.points_earned).toFixed(1)} / ${grade.assignment_points}` : '-'}
          </td>
          <td class="grade-cell ${gradeClass}">
            ${grade.percentage !== null ? `${Math.round(grade.percentage)}%` : '-'}
          </td>
          <td style="text-align: center;">
            ${grade.is_late ? '<span class="category-badge late-badge">Late</span>' : ''}
            ${grade.is_excused ? '<span class="category-badge" style="background: #dbeafe; color: #1e40af;">Excused</span>' : ''}
            ${!grade.is_late && !grade.is_excused && grade.percentage !== null ? '<span style="color: #059669;">✓</span>' : ''}
          </td>
        </tr>
      `;
    });

    // Generate missing assignments HTML
    let missingAssignmentsHtml = '';
    if (missingAssignments && missingAssignments.length > 0) {
      missingAssignments.forEach(assignment => {
        missingAssignmentsHtml += `
          <tr>
            <td>
              ${assignment.name}
              <span class="category-badge missing-badge" style="margin-left: 8px;">Missing</span>
            </td>
            <td>${assignment.category_name || 'N/A'}</td>
            <td>${assignment.points_possible}</td>
            <td>${assignment.due_date ? new Date(assignment.due_date).toLocaleDateString() : 'No due date'}</td>
          </tr>
        `;
      });
    }

    // Return just the body HTML content for the report
    return `
      <div class="report-header">
        <h1>Student Progress Report</h1>
        <h2>${gradebookData.cohort?.name || 'Class'}</h2>
        <div style="font-size: 13px; color: #64748b; margin-top: 8px;">
          Generated: ${new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </div>
      </div>

      <div class="info-grid">
        <div class="info-item">
          <div class="info-label">Student Name</div>
          <div class="info-value">${student.first_name} ${student.last_name}</div>
        </div>
        <div class="info-item">
          <div class="info-label">Student ID</div>
          <div class="info-value">${student.student_id || 'N/A'}</div>
        </div>
      </div>

      <div class="grade-summary">
        <h3>Current Grade</h3>
        <div class="grade-display">
          <div class="grade-number">${Math.round(finalGrade)}%</div>
          <div class="grade-letter">${letterGrade}</div>
        </div>
        <div class="class-comparison">
          Class Average: ${classAverage}% |
          ${finalGrade > classAverage ? ' Above' : finalGrade < classAverage ? ' Below' : ' At'} class average
        </div>
      </div>

      <div class="section">
        <div class="section-title">Performance by Category</div>
        ${Object.keys(categories).length > 0 ? `
          <div class="category-stats">
            ${categoryCardsHtml}
          </div>
        ` : '<div class="no-data">No graded assignments yet</div>'}
      </div>

      <div class="section">
        <div class="section-title">Assignment Grades</div>
        ${studentGrades.length > 0 ? `
          <table>
            <thead>
              <tr>
                <th style="width: 35%;">Assignment</th>
                <th style="width: 20%;">Category</th>
                <th style="width: 15%; text-align: center;">Score</th>
                <th style="width: 15%; text-align: center;">Grade</th>
                <th style="width: 15%; text-align: center;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${assignmentRowsHtml}
            </tbody>
          </table>
        ` : '<div class="no-data">No assignments graded yet</div>'}
      </div>

      ${missingAssignments && missingAssignments.length > 0 ? `
        <div class="section">
          <div class="section-title">Missing Assignments</div>
          <table>
            <thead>
              <tr>
                <th>Assignment</th>
                <th>Category</th>
                <th>Points Possible</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              ${missingAssignmentsHtml}
            </tbody>
          </table>
        </div>
      ` : ''}

      <div class="footer">
        <p>This report was generated electronically and reflects the student's academic performance as of the date shown above.</p>
        <p style="margin-top: 5px;">For questions or concerns, please contact the teacher directly.</p>
      </div>
    `;
  };

  const handleStudentReport = (student) => {
    if (!student || !gradebookData) return;

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow pop-ups to view the student report');
        return;
      }

      const reportBodyHTML = generateStudentReportHTML(student);

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Student Report - ${student.first_name} ${student.last_name}</title>
            <style>
              @page { size: letter portrait; margin: 0.5in; }
              body {
                font-family: 'Arial', 'Helvetica', sans-serif;
                line-height: 1.4;
                color: #1f2937;
                max-width: 8.5in;
                margin: 0 auto;
                padding: 20px;
                background: white;
              }
              .report-header {
                text-align: center;
                border-bottom: 3px solid #2563eb;
                padding-bottom: 15px;
                margin-bottom: 20px;
              }
              .report-header h1 {
                font-size: 24px;
                color: #1e40af;
                margin: 0 0 5px 0;
              }
              .report-header h2 {
                font-size: 18px;
                color: #475569;
                margin: 5px 0;
                font-weight: normal;
              }
              .info-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
                margin-bottom: 20px;
                padding: 15px;
                background: #f8fafc;
                border-radius: 8px;
              }
              .info-item { padding: 5px 0; }
              .info-label {
                font-weight: 600;
                color: #64748b;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .info-value {
                font-size: 16px;
                color: #1f2937;
                margin-top: 2px;
              }
              .grade-summary {
                background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
                color: white;
                padding: 20px;
                border-radius: 8px;
                margin-bottom: 20px;
                text-align: center;
              }
              .grade-summary h3 {
                margin: 0 0 10px 0;
                font-size: 16px;
                opacity: 0.9;
              }
              .grade-display {
                display: flex;
                justify-content: center;
                align-items: baseline;
                gap: 15px;
              }
              .grade-number {
                font-size: 48px;
                font-weight: bold;
              }
              .grade-letter {
                font-size: 32px;
                font-weight: 600;
              }
              .class-comparison {
                font-size: 13px;
                margin-top: 10px;
                opacity: 0.9;
              }
              .section {
                margin-bottom: 25px;
                page-break-inside: avoid;
              }
              .section-title {
                font-size: 16px;
                font-weight: 600;
                color: #1e40af;
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 5px;
                margin-bottom: 12px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 11px;
              }
              thead { background: #f1f5f9; }
              th {
                text-align: left;
                padding: 8px;
                font-weight: 600;
                color: #475569;
                border-bottom: 2px solid #cbd5e1;
              }
              td {
                padding: 6px 8px;
                border-bottom: 1px solid #e5e7eb;
              }
              tbody tr:hover { background: #f8fafc; }
              .grade-cell {
                text-align: center;
                font-weight: 600;
              }
              .grade-good { color: #059669; }
              .grade-warning { color: #d97706; }
              .grade-poor { color: #dc2626; }
              .category-badge {
                display: inline-block;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 10px;
                font-weight: 600;
                background: #e0e7ff;
                color: #3730a3;
              }
              .missing-badge {
                background: #fee2e2;
                color: #991b1b;
              }
              .late-badge {
                background: #fed7aa;
                color: #9a3412;
              }
              .category-stats {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 12px;
                margin-bottom: 15px;
              }
              .category-card {
                background: #f8fafc;
                padding: 12px;
                border-radius: 6px;
                border-left: 4px solid #3b82f6;
              }
              .category-card h4 {
                margin: 0 0 8px 0;
                font-size: 13px;
                color: #1e40af;
              }
              .category-percentage {
                font-size: 24px;
                font-weight: bold;
                color: #1f2937;
              }
              .category-detail {
                font-size: 11px;
                color: #64748b;
                margin-top: 4px;
              }
              .no-data {
                text-align: center;
                color: #94a3b8;
                padding: 20px;
                font-style: italic;
              }
              .footer {
                margin-top: 30px;
                padding-top: 15px;
                border-top: 1px solid #e5e7eb;
                font-size: 11px;
                color: #64748b;
                text-align: center;
              }
              .print-button {
                position: fixed;
                top: 20px;
                right: 20px;
                background: #2563eb;
                color: white;
                border: none;
                padding: 10px 20px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 600;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              .print-button:hover {
                background: #1e40af;
              }
              @media print {
                body { padding: 0; }
                .print-button { display: none !important; }
              }
            </style>
          </head>
          <body>
            <button class="print-button" onclick="window.print()">🖨️ Print Report</button>
            ${reportBodyHTML}
          </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();

      // Trigger print dialog automatically
      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      console.error('Error generating student report:', error);
      alert('Error generating report: ' + error.message);
    }
  };

  const exportClassReport = () => {
    if (!gradebookData?.students) return;
    setShowClassReport(true);
  };

  const validateAssignmentForm = () => {
    const errors = {};

    // Check required fields
    if (!assignmentForm.name || assignmentForm.name.trim() === '') {
      errors.name = 'Assignment name is required';
    }

    if (!assignmentForm.category_id || assignmentForm.category_id === '') {
      errors.category_id = 'Category is required';
    }

    if (!assignmentForm.points_possible || assignmentForm.points_possible <= 0) {
      errors.points_possible = 'Points possible must be greater than 0';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAssignment = async () => {
    // Validate form before submitting
    if (!validateAssignmentForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...assignmentForm,
          cohort_id: parseInt(selectedCohort)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create assignment');
      }

      toast({
        title: "Success",
        description: "Assignment created successfully"
      });

      setShowCreateAssignment(false);
      setFormErrors({});
      setAssignmentForm({
        name: '',
        description: '',
        category_id: '',
        points_possible: 100,
        due_date: '',
        is_hidden_from_students: false,
        allows_submissions: false,
        submission_types: []
      });

      fetchGradebookData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create assignment",
        variant: "destructive"
      });
    }
  };

  const handleEditAssignment = (assignment) => {
    console.log('Editing assignment:', assignment);
    console.log('Available categories:', categories);

    setEditingAssignment(assignment);

    // Better due date formatting - handle full datetime strings
    let formattedDueDate = '';
    if (assignment.due_date) {
      const date = new Date(assignment.due_date);
      if (!isNaN(date.getTime())) {
        // Format as datetime-local format: YYYY-MM-DDTHH:MM
        formattedDueDate = date.toISOString().slice(0, 16);
      }
    }

    const categoryId = assignment.category_id ? assignment.category_id.toString() : '';
    console.log('Setting category_id to:', categoryId, 'from assignment.category_id:', assignment.category_id);

    setAssignmentForm({
      name: assignment.name || '',
      description: assignment.description || '',
      category_id: categoryId,
      points_possible: assignment.points_possible || 100,
      due_date: formattedDueDate,
      is_hidden_from_students: assignment.is_hidden_from_students || false,
      allows_submissions: assignment.allows_submissions || false,
      submission_types: assignment.submission_types || []
    });
    setShowEditAssignment(true);
  };

  const handleUpdateAssignment = async () => {
    // Validation
    if (!assignmentForm.name?.trim()) {
      toast({
        title: "Validation Error",
        description: "Assignment name is required",
        variant: "destructive"
      });
      return;
    }

    if (!assignmentForm.points_possible || assignmentForm.points_possible <= 0) {
      toast({
        title: "Validation Error",
        description: "Points possible must be greater than 0",
        variant: "destructive"
      });
      return;
    }

    if (assignmentForm.points_possible > 1000) {
      toast({
        title: "Validation Error",
        description: "Points possible cannot exceed 1000",
        variant: "destructive"
      });
      return;
    }

    setUpdatingAssignment(true);
    setUpdateSuccess(false);

    try {
      // Prepare the data for the backend
      const updateData = {
        name: assignmentForm.name.trim(),
        description: assignmentForm.description?.trim() || '',
        category_id: assignmentForm.category_id ? parseInt(assignmentForm.category_id) : null,
        points_possible: parseFloat(assignmentForm.points_possible),
        due_date: assignmentForm.due_date || null,
        is_hidden_from_students: assignmentForm.is_hidden_from_students || false,
        allows_submissions: assignmentForm.allows_submissions || false,
        submission_types: assignmentForm.submission_types || []
      };

      console.log('Updating assignment:', editingAssignment.id, updateData);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/assignments/${editingAssignment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      console.log('Update response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update failed with error:', errorData);
        throw new Error(errorData.error || 'Failed to update assignment');
      }

      const result = await response.json();
      console.log('Update successful:', result);

      // Show success state
      setUpdateSuccess(true);

      toast({
        title: "✅ Assignment Updated!",
        description: "Changes have been saved successfully",
        duration: 3000
      });

      // Refresh data
      await fetchGradebookData();

      // Verify the assignment was updated in the data
      setTimeout(() => {
        const updatedAssignment = gradebookData?.assignments?.find(a => a.id === editingAssignment.id);
        console.log('Updated assignment in gradebook data:', updatedAssignment);
      }, 1000);

      // Close dialog after showing success
      setTimeout(() => {
        setShowEditAssignment(false);
        setEditingAssignment(null);
        setUpdateSuccess(false);
        setAssignmentForm({
          name: '',
          description: '',
          category_id: '',
          points_possible: 100,
          due_date: '',
          is_hidden_from_students: false
        });
      }, 2000);

    } catch (error) {
      console.error('Assignment update error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update assignment",
        variant: "destructive"
      });
    } finally {
      setUpdatingAssignment(false);
    }
  };

  const handleDeleteAssignment = async () => {
    if (!editingAssignment) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${editingAssignment.name}"?\n\nThis will remove the assignment and all associated grades. This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/assignments/${editingAssignment.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete assignment');
      }

      toast({
        title: "Success",
        description: `"${editingAssignment.name}" has been deleted successfully`
      });

      setShowEditAssignment(false);
      setEditingAssignment(null);
      setAssignmentForm({
        name: '',
        description: '',
        category_id: '',
        points_possible: 100,
        due_date: '',
        is_hidden_from_students: false
      });

      fetchGradebookData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete assignment",
        variant: "destructive"
      });
    }
  };

  const handleCellEdit = (studentId, assignmentId) => {
    const grade = getAssignmentGrade(studentId, assignmentId);
    setEditingCell({ studentId, assignmentId });
    // Store points earned, or special codes (nhi, exc)
    if (grade?.status === 'nhi') {
      setEditingValue('nhi');
    } else if (grade?.status === 'exc') {
      setEditingValue('exc');
    } else {
      setEditingValue(grade?.points_earned?.toString() || '');
    }

    // Focus input and select only the score part (not the " / max")
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Select only the number part before " / "
        const currentValue = grade?.points_earned?.toString() || '';
        if (currentValue) {
          // Select the existing value
          inputRef.current.setSelectionRange(0, currentValue.length);
        } else {
          // For empty/new grades, select the underscore placeholder (position 0 to 1)
          // This way typing will replace the _ instead of inserting before it
          inputRef.current.setSelectionRange(0, 1);
        }
      }
    }, 0);
  };

  const handleCellSave = async () => {
    if (!editingCell || saving) return;

    console.log('handleCellSave called:', { editingValue, editingCell, saving });

    const { studentId, assignmentId } = editingCell;
    const assignment = gradebookData.assignments.find(a => a.id === assignmentId);
    const existingGrade = getAssignmentGrade(studentId, assignmentId);
    const trimmedValue = editingValue.trim().toLowerCase();

    // Check if value actually changed - if not, just close editor to preserve auto_synced flag
    let valueChanged = true;
    if (existingGrade) {
      // Compare against existing value
      if (existingGrade.status === 'nhi' && trimmedValue === 'nhi') {
        valueChanged = false;
      } else if (existingGrade.status === 'exc' && trimmedValue === 'exc') {
        valueChanged = false;
      } else if (existingGrade.points_earned !== null &&
                 trimmedValue !== 'nhi' &&
                 trimmedValue !== 'exc' &&
                 trimmedValue !== '' &&
                 trimmedValue !== 'c') {
        // Compare numeric values
        const existingPoints = parseFloat(existingGrade.points_earned);
        const newPoints = parseFloat(editingValue);
        if (!isNaN(existingPoints) && !isNaN(newPoints) && existingPoints === newPoints) {
          valueChanged = false;
        }
      }
    } else if (trimmedValue === '' || trimmedValue === 'c') {
      // No existing grade and trying to clear - nothing to do
      valueChanged = false;
    }

    // If value didn't change, just close editor without saving
    if (!valueChanged) {
      console.log('Value unchanged, closing editor without saving to preserve auto_synced');
      setEditingCell(null);
      setEditingValue('');
      return;
    }

    // Handle empty value or 'c' for clear - delete the grade and return to "Click to grade"
    if (trimmedValue === '' || trimmedValue === 'c') {
      console.log('Clearing grade back to default');

      if (existingGrade?.id) {
        // Grade exists, delete it
        console.log('Deleting existing grade:', existingGrade.id);

        // Add to saving cells for animation
        const cellKey = `${studentId}-${assignmentId}`;
        setSavingCells(prev => new Set(prev).add(cellKey));
        setSaving(true);

        try {
          const deleteResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/grades/${existingGrade.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });

          console.log('Delete response:', deleteResponse.ok);

          if (!deleteResponse.ok) {
            throw new Error('Failed to delete grade');
          }

          await fetchGradebookData();

          // Remove from saving, add to saved for success animation
          setSavingCells(prev => {
            const next = new Set(prev);
            next.delete(cellKey);
            return next;
          });
          setSavedCells(prev => new Set(prev).add(cellKey));

          // Clear saved state after 2 seconds
          setTimeout(() => {
            setSavedCells(prev => {
              const next = new Set(prev);
              next.delete(cellKey);
              return next;
            });
          }, 2000);

          toast({
            title: "Grade Cleared",
            description: "Grade has been cleared successfully"
          });
        } catch (error) {
          console.error('Delete error:', error);

          // Remove from saving on error
          setSavingCells(prev => {
            const next = new Set(prev);
            next.delete(cellKey);
            return next;
          });

          toast({
            title: "Error",
            description: "Failed to clear grade",
            variant: "destructive"
          });
        } finally {
          setSaving(false);
        }
      } else {
        // No grade exists, just close the editor
        console.log('No existing grade, just closing editor');
      }

      setEditingCell(null);
      setEditingValue('');
      return;
    }

    // Handle special statuses
    let pointsEarned, percentage, status = null;

    if (trimmedValue === 'nhi') {
      pointsEarned = 0;
      percentage = 0;
      status = 'nhi';
    } else if (trimmedValue === 'exc') {
      pointsEarned = null;
      percentage = null;
      status = 'exc';
    } else {
      pointsEarned = parseFloat(editingValue);

      if (isNaN(pointsEarned) || pointsEarned < 0 || pointsEarned > assignment.points_possible) {
        toast({
          title: "Invalid Grade",
          description: `Please enter a number between 0 and ${Math.round(assignment.points_possible)}, or: nhi, exc, c`,
          variant: "destructive"
        });
        return;
      }

      percentage = (pointsEarned / assignment.points_possible) * 100;
    }

    // Add to saving cells for animation
    const cellKey = `${studentId}-${assignmentId}`;
    setSavingCells(prev => new Set(prev).add(cellKey));
    setSaving(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/grades`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          student_id: studentId,
          assignment_id: assignmentId,
          points_earned: pointsEarned,
          percentage: percentage,
          status: status
        })
      });

      if (!response.ok) throw new Error('Failed to save grade');

      await fetchGradebookData();
      setEditingCell(null);
      setEditingValue('');

      // Remove from saving, add to saved for success animation
      setSavingCells(prev => {
        const next = new Set(prev);
        next.delete(cellKey);
        return next;
      });
      setSavedCells(prev => new Set(prev).add(cellKey));

      // Clear saved state after 2 seconds
      setTimeout(() => {
        setSavedCells(prev => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      }, 2000);

      toast({
        title: "Grade Saved",
        description: status === 'nhi' ? 'Marked as not handed in' : status === 'exc' ? 'Marked as excused' : `Grade updated to ${percentage.toFixed(1)}%`
      });
    } catch (error) {
      // Remove from saving on error
      setSavingCells(prev => {
        const next = new Set(prev);
        next.delete(cellKey);
        return next;
      });

      toast({
        title: "Error",
        description: "Failed to save grade",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  // Release/Return grade functions
  const handleReleaseGrade = async (gradeId, studentName, assignmentName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/grades/${gradeId}/release`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to release grade');

      await fetchGradebookData();
      toast({
        title: "Grade Released",
        description: `Released ${assignmentName} for ${studentName}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to release grade",
        variant: "destructive"
      });
    }
  };

  const handleReturnGrade = async (gradeId, studentName, assignmentName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/grades/${gradeId}/return`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to return grade');

      await fetchGradebookData();
      toast({
        title: "Grade Returned",
        description: `Returned ${assignmentName} for ${studentName} (hidden from student)`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to return grade",
        variant: "destructive"
      });
    }
  };

  const handleReleaseAllForAssignment = async (assignmentId, assignmentName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/assignments/${assignmentId}/release-all`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to release all grades');

      const result = await response.json();
      await fetchGradebookData();
      toast({
        title: "All Grades Released",
        description: `Released ${result.releasedCount} grades for ${assignmentName}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to release all grades",
        variant: "destructive"
      });
    }
  };

  // Helper function to get sorted/filtered students (matches display order)
  const getSortedStudents = () => {
    if (!gradebookData) return [];

    return gradebookData.students
      .slice()
      .filter(student => {
        if (!searchFilter.trim()) return true;
        const search = searchFilter.toLowerCase();
        return (
          student.first_name.toLowerCase().includes(search) ||
          student.last_name.toLowerCase().includes(search) ||
          student.student_id.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => {
        if (sortBy === 'lastName') {
          return a.last_name.localeCompare(b.last_name);
        } else if (sortBy === 'firstName') {
          return a.first_name.localeCompare(b.first_name);
        } else if (sortBy === 'studentId') {
          return a.student_id.localeCompare(b.student_id);
        } else if (sortBy === 'gradeHighLow') {
          const gradeA = calculateStudentGrade(a.id).finalGrade;
          const gradeB = calculateStudentGrade(b.id).finalGrade;
          return gradeB - gradeA;
        } else if (sortBy === 'gradeLowHigh') {
          const gradeA = calculateStudentGrade(a.id).finalGrade;
          const gradeB = calculateStudentGrade(b.id).finalGrade;
          return gradeA - gradeB;
        }
        return 0;
      });
  };

  const navigateToCell = (direction) => {
    if (!editingCell || !gradebookData) return null;

    // Use sorted/filtered students to match display order
    const sortedStudents = getSortedStudents();
    const currentStudentIndex = sortedStudents.findIndex(s => s.id === editingCell.studentId);
    const currentAssignmentIndex = gradebookData.assignments.findIndex(a => a.id === editingCell.assignmentId);

    let newStudentIndex = currentStudentIndex;
    let newAssignmentIndex = currentAssignmentIndex;

    switch (direction) {
      case 'up':
        newStudentIndex = currentStudentIndex - 1;
        if (newStudentIndex < 0) return null;
        break;
      case 'down':
        newStudentIndex = currentStudentIndex + 1;
        if (newStudentIndex >= sortedStudents.length) return null;
        break;
      case 'right':
        newAssignmentIndex = currentAssignmentIndex + 1;
        if (newAssignmentIndex >= gradebookData.assignments.length) return null;
        break;
      case 'left':
        newAssignmentIndex = currentAssignmentIndex - 1;
        if (newAssignmentIndex < 0) return null;
        break;
      default:
        return null;
    }

    const newStudent = sortedStudents[newStudentIndex];
    const newAssignment = gradebookData.assignments[newAssignmentIndex];

    if (newStudent && newAssignment) {
      return { studentId: newStudent.id, assignmentId: newAssignment.id };
    }

    return null;
  };

  const handleKeyPress = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleCellSave();

      // Navigate up or down based on shift key
      const direction = e.shiftKey ? 'up' : 'down';
      const nextCell = navigateToCell(direction);
      if (nextCell) {
        handleCellEdit(nextCell.studentId, nextCell.assignmentId);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      await handleCellSave();

      // Navigate right or left based on shift key
      const direction = e.shiftKey ? 'left' : 'right';
      const nextCell = navigateToCell(direction);
      if (nextCell) {
        handleCellEdit(nextCell.studentId, nextCell.assignmentId);
      }
    } else if (e.key === 'Escape') {
      handleCellCancel();
    }
  };

  const getAssignmentGrade = (studentId, assignmentId) => {
    if (!gradebookData) return null;

    const entry = gradebookData.entries.find(
      e => e.student_id === studentId && e.assignment_id === assignmentId
    );

    return entry;
  };

  // Hash function for consistent color assignment
  // Using a better distribution algorithm to avoid collisions
  const hashCode = (str) => {
    if (!str) return 0;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    // Use a prime multiplier for better distribution
    return Math.abs(hash * 2654435761);
  };

  // Generate consistent professional colors for categories
  // Uses subtle -50/-100 variants instead of bright -100/-300 colors
  const getCategoryColor = (categoryName) => {
    if (!categoryName) return {
      bg: 'bg-gray-50',
      bgSolid: 'bg-gray-100',
      text: 'text-gray-700',
      border: 'border-gray-200',
      accent: 'border-gray-400'
    };

    // Professional coordinated palette - 8 colors for variety
    // Uses harmonious color families instead of random bright colors
    const themeColors = [
      {
        bg: 'bg-blue-50',        // Very subtle background
        bgSolid: 'bg-blue-100',  // Slightly stronger for released
        text: 'text-blue-700',
        border: 'border-blue-200',
        accent: 'border-blue-500',
        name: 'ocean'
      },
      {
        bg: 'bg-slate-50',
        bgSolid: 'bg-slate-100',
        text: 'text-slate-700',
        border: 'border-slate-200',
        accent: 'border-slate-500',
        name: 'graphite'
      },
      {
        bg: 'bg-violet-50',       // Replaces bright purple
        bgSolid: 'bg-violet-100',
        text: 'text-violet-700',
        border: 'border-violet-200',
        accent: 'border-violet-500',
        name: 'lavender'
      },
      {
        bg: 'bg-emerald-50',      // Replaces bright green
        bgSolid: 'bg-emerald-100',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        accent: 'border-emerald-500',
        name: 'forest'
      },
      {
        bg: 'bg-amber-50',
        bgSolid: 'bg-amber-100',
        text: 'text-amber-700',
        border: 'border-amber-200',
        accent: 'border-amber-500',
        name: 'honey'
      },
      {
        bg: 'bg-rose-50',         // Replaces bright pink
        bgSolid: 'bg-rose-100',
        text: 'text-rose-700',
        border: 'border-rose-200',
        accent: 'border-rose-500',
        name: 'coral'
      },
      {
        bg: 'bg-cyan-50',         // Replaces bright teal
        bgSolid: 'bg-cyan-100',
        text: 'text-cyan-700',
        border: 'border-cyan-200',
        accent: 'border-cyan-500',
        name: 'sky'
      },
      {
        bg: 'bg-indigo-50',
        bgSolid: 'bg-indigo-100',
        text: 'text-indigo-700',
        border: 'border-indigo-200',
        accent: 'border-indigo-500',
        name: 'twilight'
      }
    ];

    // 8 professional colors - enough for most teachers
    // All using -50/-100 variants for subtlety (not bright -100/-300)
    const index = Math.abs(hashCode(categoryName)) % themeColors.length;
    return themeColors[index];
  };

  // Tooltip helper components - minimal, user can track row/column
  const GradeCellTooltip = ({ grade, assignment, studentName }) => {
    if (!grade?.submitted_at) return null;

    return (
      <div className="text-left text-xs text-gray-200">
        Submitted: {new Date(grade.submitted_at).toLocaleDateString()}
      </div>
    );
  };

  const FinalGradeTooltip = ({ student, finalGrade, categoryBreakdown }) => {
    // Calculate useful teacher stats
    const studentGrades = gradebookData.entries?.filter(e => e.student_id === student.id) || [];
    const totalAssignments = gradebookData.assignments?.length || 0;
    const completedAssignments = studentGrades.filter(g => g.percentage !== null).length;
    const missingAssignments = totalAssignments - completedAssignments;
    const unreleasedCount = studentGrades.filter(g => !g.is_released).length;
    const completionRate = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;

    return (
      <div className="text-left space-y-2 max-w-sm">
        {/* Header */}
        <div className="border-b border-gray-700 pb-2">
          <div className="font-semibold text-white">
            {student.first_name} {student.last_name}
          </div>
        </div>

        {/* Teacher-Useful Stats */}
        <div className="space-y-1 pt-1">
          <div className="text-sm text-gray-300">
            <span className="font-medium">Completion:</span> {completedAssignments}/{totalAssignments} assignments ({completionRate}%)
          </div>

          {missingAssignments > 0 && (
            <div className="text-sm text-orange-300">
              <span className="font-medium">Missing:</span> {missingAssignments} assignment{missingAssignments !== 1 ? 's' : ''}
            </div>
          )}

          {unreleasedCount > 0 && (
            <div className="text-sm text-gray-400">
              <span className="font-medium">Unreleased:</span> {unreleasedCount} grade{unreleasedCount !== 1 ? 's' : ''}
            </div>
          )}

          {/* Average across all graded assignments */}
          {completedAssignments > 0 && (
            <div className="text-sm text-gray-300">
              <span className="font-medium">Average:</span> {Math.round(finalGrade)}% across graded work
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGradebookGrid = () => {
    if (!gradebookData || !gradebookData.students || !gradebookData.assignments) {
      return (
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg">No gradebook data available</div>
          <div className="text-sm mt-2">Students and assignments will appear here once available</div>
        </div>
      );
    }

    return (
      <div className="border rounded-lg overflow-hidden bg-white gradebook-container flex-grow flex flex-col">
        {/* Top scrollbar for easier horizontal navigation */}
        <div
          className="flex-shrink-0 overflow-x-auto overflow-y-hidden h-3 bg-gray-100 border-b"
          onScroll={(e) => {
            // Sync top scrollbar with bottom table scroll
            const bottomScroll = document.querySelector('.gradebook-table-scroll');
            if (bottomScroll) {
              bottomScroll.scrollLeft = e.target.scrollLeft;
            }
          }}
        >
          <div style={{ width: `${(gradebookData.assignments.length * 128) + 208 + 128 + 112}px`, height: '1px' }}></div>
        </div>

        <div
          className="flex-grow overflow-x-auto overflow-y-auto gradebook-table-scroll"
          onScroll={(e) => {
            // Sync bottom table scroll with top scrollbar
            const topScroll = e.target.previousElementSibling;
            if (topScroll) {
              topScroll.scrollLeft = e.target.scrollLeft;
            }
          }}
        >
          <table className="w-full text-xs">
            {/* Header - Sticky so it floats when scrolling vertically */}
            <thead className="sticky top-0 z-30 bg-gray-100 border-b shadow-sm">
              <tr>
                <th className="sticky left-0 z-40 bg-gray-100 px-2 py-1.5 text-left font-semibold text-gray-900 border-r w-48 min-w-[12rem] max-w-[12rem]">
                  <div className="flex flex-col gap-1">
                    <span>Student</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="text-xs border rounded px-1 py-0.5 bg-white"
                      title="Sort students by..."
                    >
                      <option value="lastName">Last Name</option>
                      <option value="firstName">First Name</option>
                      <option value="studentId">Student ID</option>
                      <option value="gradeHighLow">Grade (High to Low)</option>
                      <option value="gradeLowHigh">Grade (Low to High)</option>
                    </select>
                  </div>
                </th>
                {gradebookData.assignments.map(assignment => {
                  const categoryColors = getCategoryColor(assignment.category_name);

                  return (
                    <th key={assignment.id} className={`px-3 py-3 text-left font-medium bg-gray-50 border-t-4 ${categoryColors.accent} border-r border-gray-200 w-32 min-w-[8rem] max-w-[8rem] relative group/header`}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="cursor-pointer hover:bg-gray-100 transition-colors rounded -m-1 p-1"
                            onClick={() => handleEditAssignment(assignment)}
                          >
                            <div className="text-sm font-medium leading-tight text-gray-900">
                              <div className="truncate">
                                {assignment.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5 font-normal">
                                {assignment.points_possible} pts
                                {assignment.is_test_assignment && ' • Test'}
                                {assignment.is_hidden_from_students && ' • Hidden'}
                              </div>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="max-w-xs text-left">
                            <div className="font-semibold">{assignment.name}</div>
                            {assignment.description && (
                              <div className="text-xs text-gray-300 mt-1">
                                {assignment.description}
                              </div>
                            )}
                            <div className="text-xs mt-2 space-y-1">
                              <div>{assignment.points_possible} pts • {assignment.category_name}</div>
                              {assignment.is_test_assignment && <div className="text-blue-300">Auto-synced from test results</div>}
                              {assignment.is_hidden_from_students && <div className="text-amber-300">Hidden from students</div>}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                      {/* Release All button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReleaseAllForAssignment(assignment.id, assignment.name);
                        }}
                        className="absolute top-1 right-1 p-1 opacity-0 group-hover/header:opacity-100 transition-opacity bg-white rounded shadow-sm hover:bg-gray-50"
                        title="Release all grades for this assignment"
                      >
                        <Send className="h-3 w-3 text-blue-600" />
                      </button>
                    </th>
                  );
                })}
                {/* Add Assignment column */}
                <th className="bg-gray-50 w-32 min-w-[8rem] border-r border-l px-3 py-3">
                  <button
                    onClick={() => setShowCreateAssignment(true)}
                    disabled={!selectedCohort}
                    className="flex items-center justify-center gap-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full text-sm font-medium"
                    title="Add new assignment"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Assignment</span>
                  </button>
                </th>
                <th className="sticky right-0 z-20 bg-gray-50 border-l-2 border-gray-300 px-3 py-3 text-center font-semibold text-gray-900 w-28 min-w-[7rem] max-w-[7rem]">
                  Final Grade
                </th>
              </tr>
            </thead>

            {/* Body */}
            <tbody>
              {getSortedStudents().map((student, index) => {
                const { finalGrade, letterGrade } = calculateStudentGrade(student.id);
                const isEven = index % 2 === 0;

                return (
                  <tr key={student.id} className={`border-b hover:bg-gray-50 ${isEven ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className={`sticky left-0 z-10 ${isEven ? 'bg-white/95' : 'bg-gray-50/90'} border-r-2 border-gray-300 px-4 py-1 w-52 min-w-[13rem] max-w-[13rem]`}>
                      <div
                        className="cursor-pointer hover:text-blue-600 transition-colors"
                        onClick={() => handleStudentReport(student)}
                        title="Click to view student report"
                      >
                        <div className="font-medium text-sm">
                          {student.last_name}, {student.first_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {student.student_id}
                        </div>
                      </div>
                    </td>

                    {gradebookData.assignments.map(assignment => {
                      const grade = getAssignmentGrade(student.id, assignment.id);
                      const isEditing = editingCell?.studentId === student.id && editingCell?.assignmentId === assignment.id;

                      // Get category color for this assignment (creates vertical color-coded columns)
                      const categoryColors = getCategoryColor(assignment.category_name);
                      const cellBg = categoryColors.bg;  // e.g., 'bg-blue-50', 'bg-emerald-50', etc.

                      // Mute the category color if unreleased (add grey overlay)
                      const unreleasedOverlay = (grade && !grade.is_released) ? 'bg-gray-200/40' : '';
                      const cellKey = `${student.id}-${assignment.id}`;
                      const isSaving = savingCells.has(cellKey);
                      const isSaved = savedCells.has(cellKey);

                      return (
                        <td key={assignment.id} className={`px-2 py-1 text-center w-24 min-w-[6rem] max-w-[6rem] border-r border-gray-200 relative ${cellBg} ${unreleasedOverlay} hover:bg-gray-100 transition-colors ${isSaving ? 'ring-2 ring-blue-400 ring-opacity-50 animate-pulse' : ''} ${isSaved ? 'ring-2 ring-green-400 ring-opacity-50' : ''}`}>
                          {isEditing ? (
                            <div className="flex flex-col items-center justify-center">
                              <Input
                                ref={inputRef}
                                value={`${editingValue || '_'} / ${Math.round(assignment.points_possible)}`}
                                onChange={(e) => {
                                  // Extract just the score part before the " / "
                                  const fullValue = e.target.value;
                                  const scoreMatch = fullValue.match(/^([^/]*)/);
                                  let scoreValue = scoreMatch ? scoreMatch[1].trim() : '';
                                  // Remove underscore placeholder if present
                                  if (scoreValue === '_') scoreValue = '';

                                  console.log('Input onChange:', { fullValue, scoreValue });
                                  setEditingValue(scoreValue);
                                }}
                                onKeyDown={handleKeyPress}
                                onBlur={handleCellSave}
                                className="w-20 h-8 text-center text-sm"
                                disabled={saving}
                              />
                              <div className="text-xs text-gray-400 mt-1">
                                nhi, exc, or c to clear
                              </div>
                            </div>
                          ) : (
                            <div className="relative group">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className="cursor-pointer rounded min-h-[56px] flex items-center justify-center relative"
                                    onClick={() => {
                                      console.log('Editing grade:', grade);
                                      handleCellEdit(student.id, assignment.id);
                                    }}
                                  >
                                    {grade?.status === 'nhi' ? (
                                      <div className="flex flex-col items-center gap-1">
                                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-orange-50 text-orange-600 border-orange-200">
                                          NHI
                                        </Badge>
                                        <div className="text-xs text-gray-500">0%</div>
                                      </div>
                                    ) : grade?.status === 'exc' ? (
                                      <div className="flex flex-col items-center gap-1">
                                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-blue-50 text-blue-600 border-blue-200">
                                          Excused
                                        </Badge>
                                        <div className="text-xs text-gray-500">—</div>
                                      </div>
                                    ) : grade && grade.percentage !== null ? (
                                      <div className="flex flex-col items-center gap-1">
                                        <div className={`text-sm font-medium ${
                                          grade.percentage >= 90 ? 'text-green-700' :
                                          grade.percentage >= 80 ? 'text-blue-700' :
                                          grade.percentage >= 70 ? 'text-amber-700' :
                                          grade.percentage >= 60 ? 'text-orange-700' :
                                          'text-red-700'
                                        }`}>
                                          {(() => {
                                            const points = parseFloat(grade.points_earned) || 0;
                                            const total = parseFloat(assignment.points_possible) || 0;
                                            const displayPoints = points % 1 === 0 ? Math.round(points) : points.toFixed(1);
                                            const displayTotal = total % 1 === 0 ? Math.round(total) : total.toFixed(1);
                                            return `${displayPoints} / ${displayTotal}`;
                                          })()}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {Math.round(grade.percentage)}%
                                        </div>
                                        {(grade.auto_synced || grade.is_late || !grade.is_released) && (
                                          <div className="flex gap-1 flex-wrap justify-center">
                                            {grade.auto_synced && (
                                              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-blue-50 text-blue-600 border-blue-200">
                                                Auto
                                              </Badge>
                                            )}
                                            {grade.is_late && (
                                              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-red-50 text-red-600 border-red-200">
                                                Late
                                              </Badge>
                                            )}
                                            {!grade.is_released && (
                                              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-gray-100 text-gray-600 border-gray-300">
                                                Unreleased
                                              </Badge>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 text-sm">
                                        {assignment.is_test_assignment ? '—' : 'Click to grade'}
                                      </span>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <GradeCellTooltip grade={grade} assignment={assignment} studentName={`${student.first_name} ${student.last_name}`} />
                              </Tooltip>
                              {/* Release/Return toggle - always visible for quick scanning */}
                              {grade?.id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (grade.is_released) {
                                      handleReturnGrade(grade.id, `${student.first_name} ${student.last_name}`, assignment.name);
                                    } else {
                                      handleReleaseGrade(grade.id, `${student.first_name} ${student.last_name}`, assignment.name);
                                    }
                                  }}
                                  className="absolute top-0.5 right-0.5 p-0.5 rounded hover:bg-white/50 transition-all"
                                  title={grade.is_released ? 'Released - Click to return (hide from student)' : 'Unreleased - Click to release to student'}
                                >
                                  {grade.is_released ? (
                                    <Eye className="h-3.5 w-3.5 text-green-600" />
                                  ) : (
                                    <EyeOff className="h-3.5 w-3.5 text-gray-500" />
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}

                    {/* Add Assignment column - empty for student rows */}
                    <td className="bg-gray-50 w-24 min-w-[6rem] border-r border-l"></td>

                    <td className="sticky right-0 z-10 bg-gray-50 px-3 py-2 text-center border-l-2 border-gray-300 w-28 min-w-[7rem] max-w-[7rem]">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="space-y-1 cursor-pointer">
                            {(() => {
                              const displayMode = gradebookData.settings?.display_mode || 'percentage';

                              // Performance-based color
                              const gradeColor = finalGrade >= 90 ? 'text-green-700' :
                                                 finalGrade >= 80 ? 'text-blue-700' :
                                                 finalGrade >= 70 ? 'text-amber-700' :
                                                 finalGrade >= 60 ? 'text-orange-700' :
                                                 'text-red-700';

                              // Proficiency mode: Show "75 Proficient" format
                              if (displayMode === 'proficiency') {
                                return (
                                  <div className={`text-lg font-bold ${gradeColor}`}>
                                    {finalGrade} {letterGrade}
                                  </div>
                                );
                              }

                              // Percentage mode: Show "90% A" format (always show letter grade in percentage mode)
                              else if (displayMode === 'percentage' || displayMode === 'none') {
                                return (
                                  <div className={`text-lg font-bold ${gradeColor}`}>
                                    {finalGrade}% {letterGrade}
                                  </div>
                                );
                              }

                              // Fallback: show both (should rarely hit this)
                              else {
                                return (
                                  <>
                                    <div className={`text-lg font-bold ${gradeColor}`}>
                                      {finalGrade}%
                                    </div>
                                    <div className="text-sm text-gray-700 font-medium">
                                      {letterGrade}
                                    </div>
                                  </>
                                );
                              }
                            })()}
                          </div>
                        </TooltipTrigger>
                        <FinalGradeTooltip student={student} calculateStudentGrade={calculateStudentGrade} />
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Compact grade display used inside the phone card layout (no tooltips/table chrome)
  const renderMobileGradeValue = (grade, assignment) => {
    if (grade?.status === 'nhi') {
      return (
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-orange-50 text-orange-600 border-orange-200">NHI</Badge>
          <span className="text-xs text-gray-500">0%</span>
        </div>
      );
    }
    if (grade?.status === 'exc') {
      return (
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-blue-50 text-blue-600 border-blue-200">Excused</Badge>
          <span className="text-xs text-gray-500">—</span>
        </div>
      );
    }
    if (grade && grade.percentage !== null) {
      const points = parseFloat(grade.points_earned) || 0;
      const total = parseFloat(assignment.points_possible) || 0;
      const displayPoints = points % 1 === 0 ? Math.round(points) : points.toFixed(1);
      const displayTotal = total % 1 === 0 ? Math.round(total) : total.toFixed(1);
      const color = grade.percentage >= 90 ? 'text-green-700' :
                    grade.percentage >= 80 ? 'text-blue-700' :
                    grade.percentage >= 70 ? 'text-amber-700' :
                    grade.percentage >= 60 ? 'text-orange-700' :
                    'text-red-700';
      return (
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${color}`}>{displayPoints} / {displayTotal}</span>
          <span className="text-xs text-gray-500">{Math.round(grade.percentage)}%</span>
          {!grade.is_released && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 bg-gray-100 text-gray-600 border-gray-300">Unreleased</Badge>
          )}
        </div>
      );
    }
    return <span className="text-sm text-blue-600">{assignment.is_test_assignment ? '—' : 'Tap to grade'}</span>;
  };

  // Phone layout: one card per student instead of the wide spreadsheet (which stays for md+).
  // Reuses the same edit/sort/release handlers as the table so behaviour is identical.
  const renderMobileGradebookCards = () => {
    if (!gradebookData || !gradebookData.students || !gradebookData.assignments) {
      return (
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg">No gradebook data available</div>
          <div className="text-sm mt-2">Students and assignments will appear here once available</div>
        </div>
      );
    }

    const displayMode = gradebookData.settings?.display_mode || 'percentage';

    return (
      <div className="space-y-3 pb-4">
        <Button
          variant="outline"
          onClick={() => setShowCreateAssignment(true)}
          disabled={!selectedCohort}
          className="w-full min-h-[44px]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Assignment
        </Button>

        {getSortedStudents().map(student => {
          const { finalGrade, letterGrade } = calculateStudentGrade(student.id);
          const gradeColor = finalGrade >= 90 ? 'text-green-700' :
                             finalGrade >= 80 ? 'text-blue-700' :
                             finalGrade >= 70 ? 'text-amber-700' :
                             finalGrade >= 60 ? 'text-orange-700' :
                             'text-red-700';

          return (
            <Card key={student.id} className="overflow-hidden">
              <CardContent className="p-4">
                {/* Student header */}
                <div className="flex items-start justify-between gap-3 pb-3 mb-1 border-b">
                  <div
                    className="min-w-0 cursor-pointer"
                    onClick={() => handleStudentReport(student)}
                    title="View student report"
                  >
                    <div className="font-semibold text-base text-gray-900 truncate">
                      {student.last_name}, {student.first_name}
                    </div>
                    <div className="text-xs text-gray-500">{student.student_id}</div>
                  </div>
                  <div className={`text-right flex-shrink-0 ${gradeColor}`}>
                    <div className="text-xl font-bold leading-none">
                      {finalGrade}{displayMode === 'proficiency' ? '' : '%'}
                    </div>
                    <div className="text-sm font-medium mt-0.5">{letterGrade}</div>
                  </div>
                </div>

                {/* Per-assignment rows */}
                {gradebookData.assignments.map(assignment => {
                  const grade = getAssignmentGrade(student.id, assignment.id);
                  const isEditing = editingCell?.studentId === student.id && editingCell?.assignmentId === assignment.id;
                  const categoryColors = getCategoryColor(assignment.category_name);
                  const cellKey = `${student.id}-${assignment.id}`;
                  const isSaving = savingCells.has(cellKey);
                  const isSaved = savedCells.has(cellKey);

                  return (
                    <div
                      key={assignment.id}
                      className={`flex items-center justify-between gap-3 py-2 min-h-[44px] border-b border-gray-100 last:border-b-0 ${isSaving ? 'bg-blue-50' : isSaved ? 'bg-green-50' : ''}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{assignment.name}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-xs text-gray-500">{assignment.points_possible} pts</span>
                          {assignment.category_name && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${categoryColors.bg} ${categoryColors.text}`}>
                              {assignment.category_name}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex-shrink-0 flex items-center gap-1">
                        {isEditing ? (
                          <Input
                            ref={inputRef}
                            value={`${editingValue || '_'} / ${Math.round(assignment.points_possible)}`}
                            onChange={(e) => {
                              const fullValue = e.target.value;
                              const scoreMatch = fullValue.match(/^([^/]*)/);
                              let scoreValue = scoreMatch ? scoreMatch[1].trim() : '';
                              if (scoreValue === '_') scoreValue = '';
                              setEditingValue(scoreValue);
                            }}
                            onKeyDown={handleKeyPress}
                            onBlur={handleCellSave}
                            className="w-24 h-9 text-center text-sm"
                            disabled={saving}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleCellEdit(student.id, assignment.id)}
                            className="flex items-center gap-2 min-h-[44px] px-1"
                          >
                            {renderMobileGradeValue(grade, assignment)}
                          </button>
                        )}
                        {!isEditing && grade?.id && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (grade.is_released) {
                                handleReturnGrade(grade.id, `${student.first_name} ${student.last_name}`, assignment.name);
                              } else {
                                handleReleaseGrade(grade.id, `${student.first_name} ${student.last_name}`, assignment.name);
                              }
                            }}
                            className="p-2 rounded hover:bg-gray-100"
                            title={grade.is_released ? 'Released - tap to hide from student' : 'Unreleased - tap to release to student'}
                          >
                            {grade.is_released ? (
                              <Eye className="h-4 w-4 text-green-600" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-gray-500" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  if (loading && !gradebookData) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading gradebook...</span>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={isFullscreen ? "h-screen flex flex-col overflow-hidden" : "h-full flex flex-col overflow-hidden"}>
        {/* Exit Fullscreen Button - Always visible in fullscreen */}
        {isFullscreen && (
        <Button
          onClick={toggleFullscreen}
          className="fixed top-4 right-4 z-50 shadow-lg"
          size="sm"
        >
          <Minimize className="h-4 w-4 mr-2" />
          Exit Fullscreen
        </Button>
      )}

      {/* Header Controls - Hidden in fullscreen */}
      {!isFullscreen && (
        <div className="flex-shrink-0 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <Select value={selectedCohort} onValueChange={setSelectedCohort}>
            <SelectTrigger className="w-full sm:w-64">
              <SelectValue placeholder="Select a class" />
            </SelectTrigger>
            <SelectContent>
              {cohorts.map(cohort => (
                <SelectItem key={cohort.id} value={cohort.id.toString()}>
                  {cohort.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedCohort && (
            <Badge variant="outline" className="text-sm">
              {gradebookData?.students?.length || 0} students
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="text"
            placeholder="Search students..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full sm:w-48 h-9"
            disabled={!selectedCohort}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(true)}
            disabled={!selectedCohort}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStats(!showStats)}
            disabled={!selectedCohort}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            {showStats ? 'Hide' : 'Show'} Stats
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowKeyboardHints(!showKeyboardHints)}
            disabled={!selectedCohort}
            title={showKeyboardHints ? 'Hide keyboard shortcuts' : 'Show keyboard shortcuts'}
          >
            {showKeyboardHints ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            disabled={!selectedCohort}
            title={isFullscreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}
          >
            {isFullscreen ? <Minimize className="h-4 w-4 mr-2" /> : <Maximize className="h-4 w-4 mr-2" />}
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={!selectedCohort}
            className="no-print"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrintAllReports}
            disabled={!selectedCohort}
            className="no-print"
          >
            <FileText className="h-4 w-4 mr-2" />
            All Reports
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!selectedCohort} className="no-print">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToCSV}>
                <FileText className="h-4 w-4 mr-2" />
                Export to CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportClassReport}>
                <FileText className="h-4 w-4 mr-2" />
                Class Report
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <Upload className="h-4 w-4 mr-2" />
                Import Grades (Coming Soon)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      )}

      {/* Keyboard shortcut hint */}
      {!isFullscreen && selectedCohort && gradebookData && showKeyboardHints && (
        <div className="hidden md:block flex-shrink-0 text-xs text-gray-500 text-center py-2 bg-blue-50 border-y border-blue-100">
          💡 <strong>Keyboard Shortcuts:</strong> <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">Shift + ←→</kbd> to scroll • <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">↑↓←→</kbd> to navigate • <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">Enter</kbd> to edit • <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">Tab</kbd> / <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">Shift+Tab</kbd> to move • <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">Esc</kbd> to cancel
        </div>
      )}

      {/* Main Content */}
      {selectedCohort ? (
        <div className="flex-grow flex flex-col overflow-hidden px-4">
          {/* Optional Stats Panel - Moved to Top */}
          {!isFullscreen && showStats && gradebookData && (
            <div className="flex-shrink-0 grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {gradebookData.students?.length || 0}
                    </div>
                    <div className="text-sm text-gray-600">Students</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {gradebookData.assignments?.length || 0}
                    </div>
                    <div className="text-sm text-gray-600">Assignments</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {gradebookData.students ?
                        Math.round(gradebookData.students.reduce((sum, student) => {
                          const grade = calculateStudentGrade(student.id);
                          return sum + grade.finalGrade;
                        }, 0) / gradebookData.students.length * 10) / 10 : 0}%
                    </div>
                    <div className="text-sm text-gray-600">Class Average</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {gradebookData.students ?
                        Math.round(gradebookData.students.filter(student => {
                          const grade = calculateStudentGrade(student.id);
                          return grade.finalGrade >= 70;
                        }).length / gradebookData.students.length * 100) : 0}%
                    </div>
                    <div className="text-sm text-gray-600" title="Percentage of students with final grade ≥ 70%">
                      Passing Rate (≥70%)
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Spreadsheet-style Gradebook (tablet/desktop) — already horizontally
              scrollable with a sticky first column, so it stays for md+ */}
          <div className="hidden md:flex flex-grow overflow-hidden flex-col">
            {renderGradebookGrid()}
          </div>

          {/* Card-per-student layout (phone) */}
          <div className="md:hidden flex-grow overflow-y-auto">
            {renderMobileGradebookCards()}
          </div>
        </div>
      ) : (
        <Card className="m-4">
          <CardContent className="text-center py-12">
            <p className="text-gray-500 text-lg">Select a class to view the gradebook</p>
            <p className="text-gray-400 text-sm mt-2">Choose a class from the dropdown above to get started</p>
          </CardContent>
        </Card>
      )}

      {/* Create Assignment Dialog */}
      <Dialog open={showCreateAssignment} onOpenChange={(open) => {
        setShowCreateAssignment(open);
        if (!open) setFormErrors({});
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Assignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="assignment-name" className="flex items-center gap-1">
                Assignment Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="assignment-name"
                value={assignmentForm.name}
                onChange={(e) => {
                  setAssignmentForm(prev => ({ ...prev, name: e.target.value }));
                  if (formErrors.name) setFormErrors(prev => ({ ...prev, name: undefined }));
                }}
                placeholder="Enter assignment name"
                className={formErrors.name ? 'border-red-500' : ''}
              />
              {formErrors.name && (
                <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>
              )}
            </div>
            <div>
              <Label htmlFor="assignment-description">Description</Label>
              <Input
                id="assignment-description"
                value={assignmentForm.description}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Assignment description (optional)"
              />
            </div>
            <div>
              <Label htmlFor="assignment-category" className="flex items-center gap-1">
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={assignmentForm.category_id}
                onValueChange={(value) => {
                  setAssignmentForm(prev => ({ ...prev, category_id: value }));
                  if (formErrors.category_id) setFormErrors(prev => ({ ...prev, category_id: undefined }));
                }}
              >
                <SelectTrigger className={formErrors.category_id ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.category_id && (
                <p className="text-sm text-red-500 mt-1">{formErrors.category_id}</p>
              )}
            </div>
            <div>
              <Label htmlFor="assignment-points" className="flex items-center gap-1">
                Points Possible <span className="text-red-500">*</span>
              </Label>
              <Input
                id="assignment-points"
                type="number"
                value={assignmentForm.points_possible}
                onChange={(e) => {
                  setAssignmentForm(prev => ({ ...prev, points_possible: parseFloat(e.target.value) || 0 }));
                  if (formErrors.points_possible) setFormErrors(prev => ({ ...prev, points_possible: undefined }));
                }}
                min="0"
                step="0.5"
                className={formErrors.points_possible ? 'border-red-500' : ''}
              />
              {formErrors.points_possible && (
                <p className="text-sm text-red-500 mt-1">{formErrors.points_possible}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="assignment-hidden"
                type="checkbox"
                checked={assignmentForm.is_hidden_from_students}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, is_hidden_from_students: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="assignment-hidden" className="cursor-pointer">
                Hide from students
                <span className="block text-xs text-gray-500 font-normal">
                  Hidden assignments are used in grade calculations but not visible to students
                </span>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="assignment-submissions"
                type="checkbox"
                checked={assignmentForm.allows_submissions}
                onChange={(e) => setAssignmentForm(prev => ({
                  ...prev,
                  allows_submissions: e.target.checked,
                  submission_types: e.target.checked ? ['file'] : []
                }))}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="assignment-submissions" className="cursor-pointer">
                Allow student submissions
                <span className="block text-xs text-gray-500 font-normal">
                  Students can upload files, text, or URLs for this assignment
                </span>
              </Label>
            </div>
            {assignmentForm.allows_submissions && (
              <div className="ml-6 space-y-2 p-3 bg-gray-50 rounded-md">
                <Label className="text-sm font-semibold">Submission Types</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      id="submission-file"
                      type="checkbox"
                      checked={assignmentForm.submission_types.includes('file')}
                      onChange={(e) => {
                        const types = e.target.checked
                          ? [...assignmentForm.submission_types, 'file']
                          : assignmentForm.submission_types.filter(t => t !== 'file');
                        setAssignmentForm(prev => ({ ...prev, submission_types: types }));
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="submission-file" className="cursor-pointer text-sm">
                      File Upload (images, PDF, Word, Excel, etc.)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      id="submission-text"
                      type="checkbox"
                      checked={assignmentForm.submission_types.includes('text')}
                      onChange={(e) => {
                        const types = e.target.checked
                          ? [...assignmentForm.submission_types, 'text']
                          : assignmentForm.submission_types.filter(t => t !== 'text');
                        setAssignmentForm(prev => ({ ...prev, submission_types: types }));
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="submission-text" className="cursor-pointer text-sm">
                      Text Submission
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      id="submission-url"
                      type="checkbox"
                      checked={assignmentForm.submission_types.includes('url')}
                      onChange={(e) => {
                        const types = e.target.checked
                          ? [...assignmentForm.submission_types, 'url']
                          : assignmentForm.submission_types.filter(t => t !== 'url');
                        setAssignmentForm(prev => ({ ...prev, submission_types: types }));
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="submission-url" className="cursor-pointer text-sm">
                      URL/Link Submission
                    </Label>
                  </div>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="assignment-due">Due Date (Optional)</Label>
              <Input
                id="assignment-due"
                type="datetime-local"
                value={assignmentForm.due_date}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAssignment(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAssignment}>
              Create Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Assignment Dialog */}
      <Dialog open={showEditAssignment} onOpenChange={setShowEditAssignment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Assignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-assignment-name">Assignment Name</Label>
              <Input
                id="edit-assignment-name"
                value={assignmentForm.name}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter assignment name"
              />
            </div>
            <div>
              <Label htmlFor="edit-assignment-description">Description</Label>
              <Input
                id="edit-assignment-description"
                value={assignmentForm.description}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Assignment description (optional)"
              />
            </div>
            <div>
              <Label htmlFor="edit-assignment-category">Category</Label>
              <Select
                value={assignmentForm.category_id}
                onValueChange={(value) => {
                  console.log('Category changed to:', value);
                  setAssignmentForm(prev => ({ ...prev, category_id: value }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-gray-500 mt-1">
                Current value: {assignmentForm.category_id || 'none'}
              </div>
            </div>
            <div>
              <Label htmlFor="edit-assignment-points">Points Possible</Label>
              <Input
                id="edit-assignment-points"
                type="number"
                value={assignmentForm.points_possible}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, points_possible: parseFloat(e.target.value) || 0 }))}
                min="0"
                step="0.5"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="edit-assignment-hidden"
                type="checkbox"
                checked={assignmentForm.is_hidden_from_students}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, is_hidden_from_students: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="edit-assignment-hidden" className="cursor-pointer">
                Hide from students
                <span className="block text-xs text-gray-500 font-normal">
                  Hidden assignments are used in grade calculations but not visible to students
                </span>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="edit-assignment-submissions"
                type="checkbox"
                checked={assignmentForm.allows_submissions}
                onChange={(e) => setAssignmentForm(prev => ({
                  ...prev,
                  allows_submissions: e.target.checked,
                  submission_types: e.target.checked ? (prev.submission_types.length > 0 ? prev.submission_types : ['file']) : []
                }))}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="edit-assignment-submissions" className="cursor-pointer">
                Allow student submissions
                <span className="block text-xs text-gray-500 font-normal">
                  Students can upload files, text, or URLs for this assignment
                </span>
              </Label>
            </div>
            {assignmentForm.allows_submissions && (
              <div className="ml-6 space-y-2 p-3 bg-gray-50 rounded-md">
                <Label className="text-sm font-semibold">Submission Types</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      id="edit-submission-file"
                      type="checkbox"
                      checked={assignmentForm.submission_types.includes('file')}
                      onChange={(e) => {
                        const types = e.target.checked
                          ? [...assignmentForm.submission_types, 'file']
                          : assignmentForm.submission_types.filter(t => t !== 'file');
                        setAssignmentForm(prev => ({ ...prev, submission_types: types }));
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="edit-submission-file" className="cursor-pointer text-sm">
                      File Upload (images, PDF, Word, Excel, etc.)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      id="edit-submission-text"
                      type="checkbox"
                      checked={assignmentForm.submission_types.includes('text')}
                      onChange={(e) => {
                        const types = e.target.checked
                          ? [...assignmentForm.submission_types, 'text']
                          : assignmentForm.submission_types.filter(t => t !== 'text');
                        setAssignmentForm(prev => ({ ...prev, submission_types: types }));
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="edit-submission-text" className="cursor-pointer text-sm">
                      Text Submission
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      id="edit-submission-url"
                      type="checkbox"
                      checked={assignmentForm.submission_types.includes('url')}
                      onChange={(e) => {
                        const types = e.target.checked
                          ? [...assignmentForm.submission_types, 'url']
                          : assignmentForm.submission_types.filter(t => t !== 'url');
                        setAssignmentForm(prev => ({ ...prev, submission_types: types }));
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="edit-submission-url" className="cursor-pointer text-sm">
                      URL/Link Submission
                    </Label>
                  </div>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="edit-assignment-due">Due Date (Optional)</Label>
              <Input
                id="edit-assignment-due"
                type="datetime-local"
                value={assignmentForm.due_date}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <Button
              variant="destructive"
              onClick={handleDeleteAssignment}
              className="mr-auto"
            >
              Delete Assignment
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEditAssignment(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateAssignment}
                disabled={updatingAssignment}
                className={`
                  ${updateSuccess ? 'bg-green-600 hover:bg-green-700' : ''}
                `}
              >
                {updatingAssignment ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : updateSuccess ? (
                  <>
                    <span className="h-4 w-4 mr-2">✅</span>
                    Updated!
                  </>
                ) : (
                  'Update Assignment'
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gradebook Settings Dialog */}
      {showSettings && (
        <GradebookSettings
          cohortId={selectedCohort}
          onClose={() => setShowSettings(false)}
          onSettingsSaved={() => fetchGradebookData()}
        />
      )}

      {/* Class Report Dialog */}
      <ClassReportDialog
        open={showClassReport}
        onClose={() => setShowClassReport(false)}
        gradebookData={gradebookData}
        calculateStudentGrade={calculateStudentGrade}
      />
    </div>
  </TooltipProvider>
  );
};

export default GradebookView;